<template>
  <div class="ac-wrap">
    <!-- 左侧：已配置值字段（拖拽源，引用「聚合后」的值） + 已配置聚合计算值（拖拽源，嵌套引用） -->
    <div class="ac-left">
      <div class="ac-section-title">{{ $t('值字段') }}</div>
      <a-input
        v-model:value="valueSearch"
        size="small"
        allow-clear
        class="ac-search"
        :placeholder="$t('搜索值字段')"
      >
        <template #prefix>
          <search-outlined />
        </template>
      </a-input>
      <div class="ac-field-pool ac-field-pool-values">
        <div
          v-for="vf in displayValueFields"
          :key="vf.id"
          class="ac-field-item"
          :draggable="true"
          @dragstart="onValueDragStart(vf)"
          @dragend="onDragEnd"
          :title="vf.label"
        >
          <database-outlined class="ac-field-icon" />
          <span class="ac-field-label">{{ vf.label }}</span>
        </div>
        <div
          v-if="displayValueFields.length === 0"
          class="ac-empty"
        >
          {{ $t('暂无可选值字段') }}
        </div>
      </div>

      <div class="ac-section-title ac-agg-section-title">{{ $t('聚合计算值') }}</div>
      <a-input
        v-model:value="aggSearch"
        size="small"
        allow-clear
        class="ac-search"
        :placeholder="$t('搜索聚合计算值')"
      >
        <template #prefix>
          <search-outlined />
        </template>
      </a-input>
      <div class="ac-field-pool">
        <div
          v-for="ac in displayAggComputeds"
          :key="ac.id"
          class="ac-field-item ac-agg-item"
          :draggable="true"
          @dragstart="onAggComputedDragStart(ac)"
          @dragend="onDragEnd"
          :title="ac.label"
        >
          <calculator-outlined class="ac-field-icon ac-agg-icon" />
          <span class="ac-field-label">{{ ac.label }}</span>
        </div>
        <div
          v-if="displayAggComputeds.length === 0"
          class="ac-empty"
        >
          {{ $t('暂无可引用聚合计算值（可先添加其它聚合计算值，再互相嵌套引用）') }}
        </div>
      </div>
    </div>

    <!-- 右侧：名称 + 显示格式 + 表达式编辑器（可引用聚合后的值字段与其它聚合计算值，不可选子表达式） -->
    <div class="ac-right">
      <div class="ac-name-row">
        <span class="ac-name-label">{{ $t('名称') }}</span>
        <a-input
          v-model:value="local.name"
          size="small"
          class="ac-name-input"
          :placeholder="$t('请输入聚合计算值名称（将作为列标题 / 图例名）')"
          @change="emitChange"
        />
      </div>

      <!-- 显示格式（与值字段对齐） -->
      <div class="ac-format-row">
        <span class="ac-format-label">{{ $t('显示格式') }}</span>
        <a-select
          v-model:value="local.formatFun"
          size="small"
          style="width: 140px"
          :options="valueFormatOptions"
          :placeholder="$t('显示格式')"
          :allow-clear="true"
          @change="onFormatFunChange"
          @click.stop
        />
        <a-auto-complete
          v-if="local.formatFun === NUMFMT_FORMAT_KEY"
          v-model:value="local.formatPattern"
          size="small"
          style="width: 234px"
          :options="numfmtPatternOptions"
          :placeholder="$t('数据格式，如 #,##0.00')"
          @change="onPatternChange"
          @click.stop
        />
      </div>

      <div
        v-if="showTotalAggregationMode"
        class="ac-format-row"
      >
        <span class="ac-format-label">{{ $t('合计算法') }}</span>
        <a-select
          v-model:value="local.totalAggregationMode"
          size="small"
          style="width: 280px"
          :options="totalAggregationModeOptions"
          @change="emitChange"
          @click.stop
        />
      </div>

      <valueExprEditor
        v-model="local.expression"
        :field-options="[]"
        :sub-expressions="[]"
        :value-field-options="valueFieldOptions"
        :show-value-panel="false"
        :agg-computed-options="aggComputedRefOptions"
        :exclude-agg-computed-id="local.id"
        :show-agg-computed-panel="false"
        :placeholder="$t('从左侧拖拽值字段或聚合计算值，构建基于聚合后值的表达式（可嵌套引用其它聚合计算值）')"
        @change="emitChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue';
import { cloneDeep, debounce } from 'lodash';
import { CalculatorOutlined, DatabaseOutlined, SearchOutlined } from '@ant-design/icons-vue';
import {
  PivotAggComputedValue,
  PivotAggComputedRefOption,
  ExprLocalDrag,
} from '../typing';
import {
  VALUE_FORMATS,
  NUMFMT_FORMAT_KEY,
  DEFAULT_NUMFMT_PATTERN,
  NUMFMT_PATTERN_PRESETS,
} from '../func';
import ValueExprEditor from './valueExprEditor.vue';

interface Props {
  modelValue: PivotAggComputedValue;
  // 当前配置中已配置的值字段（作为 value token 引用源），每项含 id 与展示 label
  valueFieldOptions: { id: string; label: string }[];
  // 当前配置中已配置的聚合计算值（作为 aggcomputed token 引用源，含表达式供环形依赖校验）。
  // 编辑自身时由本组件与 valueExprEditor 按 id 排除，避免直接自引用。
  aggComputedOptions?: PivotAggComputedRefOption[];
  showTotalAggregationMode?: boolean;
}
const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: PivotAggComputedValue): void;
  (e: 'change'): void;
}>();

// 跨子组件共享的拖拽负载（值字段从左侧池拖出，落到编辑器画布）
const localDrag = ref<ExprLocalDrag | null>(null);
provide('vfLocalDrag', localDrag);

const local = ref<PivotAggComputedValue>(cloneDeep(props.modelValue));
watch(
  () => props.modelValue,
  v => {
    if (v) {
      local.value = cloneDeep(v);
      if (!local.value.totalAggregationMode) local.value.totalAggregationMode = 'sum_leaf';
    }
  },
  { immediate: true, deep: false },
);

const valueFormatOptions = computed(() => VALUE_FORMATS.map(f => ({ label: f.label, value: f.key })));
const numfmtPatternOptions = NUMFMT_PATTERN_PRESETS.map(p => ({
  value: p.value,
  label: p.label || p.value,
}));
const totalAggregationModeOptions = [
  { label: '按合计值重算', value: 'recompute' },
  { label: '对子单元结果求和', value: 'sum_leaf' },
];

const valueSearch = ref('');
const displayValueFields = computed<{ id: string; label: string }[]>(() => {
  const q = (valueSearch.value || '').trim().toLowerCase();
  const list = props.valueFieldOptions || [];
  if (!q) return list;
  return list.filter(v => v.label.toLowerCase().includes(q) || v.id.toLowerCase().includes(q));
});

// 可引用的聚合计算值：排除正在编辑的自身（避免直接自引用；环形依赖由前端校验 + 后端展开深度上限双重兜底）
const aggSearch = ref('');
const availableAggComputeds = computed<PivotAggComputedRefOption[]>(() =>
  (props.aggComputedOptions || []).filter(a => a.id !== local.value.id),
);
const displayAggComputeds = computed<PivotAggComputedRefOption[]>(() => {
  const q = (aggSearch.value || '').trim().toLowerCase();
  const list = availableAggComputeds.value;
  if (!q) return list;
  return list.filter(a => a.label.toLowerCase().includes(q) || a.id.toLowerCase().includes(q));
});
// 透传给 valueExprEditor 的完整引用列表（含正在编辑的自身，由 valueExprEditor 的 excludeAggComputedId 排除；
// 自身表达式参与环形依赖检测，故不能在此处过滤掉）
const aggComputedRefOptions = computed<PivotAggComputedRefOption[]>(
  () => props.aggComputedOptions || [],
);

const emitChange = () => {
  emit('update:modelValue', cloneDeep(local.value));
  emit('change');
};

const onValueDragStart = (vf: { id: string; label: string }) => {
  localDrag.value = { kind: 'value', id: vf.id, name: vf.label };
};
const onAggComputedDragStart = (ac: PivotAggComputedRefOption) => {
  localDrag.value = { kind: 'aggcomputed', id: ac.id, name: ac.label };
};
const onDragEnd = () => {
  localDrag.value = null;
};

// 切换显示格式：选中「通用数字格式」且尚无格式串时填入缺省值
const onFormatFunChange = () => {
  if (local.value.formatFun === NUMFMT_FORMAT_KEY && !local.value.formatPattern) {
    local.value.formatPattern = DEFAULT_NUMFMT_PATTERN;
  }
  emitChange();
};
// 格式串输入防抖：避免逐字符输入触发接口调用
const onPatternChange = debounce(() => emitChange(), 500);

// 对外暴露：是否具备可保存条件（名称非空）
defineExpose({
  isValid: () => !!(local.value.name && local.value.name.trim()),
});
</script>

<style lang="less" scoped>
.ac-wrap {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 360px;
}

.ac-left {
  width: 240px;
  flex-shrink: 0;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .ac-section-title {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 13px;
  }
  .ac-agg-section-title {
    margin-top: 12px;
  }
  .ac-search {
    margin-bottom: 8px;
    flex-shrink: 0;
  }
  .ac-field-pool {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  }
  .ac-field-pool-values {
    flex: 0 0 auto;
    max-height: 40%;
  }
  .ac-field-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    cursor: grab;
    font-size: 13px;
    transition: all 0.2s;
    user-select: none;
    &:hover {
      border-color: #08979c;
      background: #e6fffb;
    }
    &:active {
      cursor: grabbing;
    }
    .ac-field-icon {
      color: #08979c;
      font-size: 12px;
    }
    .ac-field-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  // 聚合计算值引用项：geekblue 色系，与值字段(cyan)区分
  .ac-agg-item {
    &:hover {
      border-color: #2f54eb;
      background: #f0f5ff;
    }
    .ac-agg-icon {
      color: #2f54eb;
    }
  }
  .ac-empty {
    text-align: center;
    color: #999;
    font-size: 12px;
    padding: 20px 0;
  }
}

.ac-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;

  .ac-name-row {
    display: flex;
    align-items: center;
    gap: 8px;
    .ac-name-label {
      font-size: 13px;
      color: #333;
      flex-shrink: 0;
      font-weight: 600;
    }
    .ac-name-input {
      flex: 1;
      max-width: 360px;
    }
  }
  .ac-format-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    .ac-format-label {
      font-size: 13px;
      color: #333;
      flex-shrink: 0;
      font-weight: 600;
    }
  }
}
</style>
